import React, { useEffect, useState, useMemo } from "react"
import { useSearchspringResults } from "@app/scripts/hooks/use-searchspring-results"
import { CardProduct } from "@app/scripts/components/card-product"
import { SearchspringSearchResult, useShopifyPriceData } from "@app/types"
import { session } from "@app/scripts/lib/session"
import { useSwatchCache } from "../hooks/use-swatches"
import { currentCountryCurrency } from "@app/scripts/lib/utils"
import {
  useShopifyPrice,
  mergeResultsWithPricing,
} from "@app/scripts/hooks/use-shopify"

export function ResultGrid(searchPage = false) {
  const [modelToggle, setModelToggle] = useState(true)

  // Pricing cache to avoid re-fetching prices for products already loaded
  const [pricingCache, setPricingCache] = useState<useShopifyPriceData[]>([])

  const results = useSearchspringResults()
  const swatchColourData = useSwatchCache(results)

  // Formats data for useShopifyPrice (Removes string results (call outs))
  const formattedProductData = useMemo(
    () =>
      results
        .map((result) => ({
          price: result.price || 0,
          compareAtPrice: result.msrp || 0,
          id: result.ss_id || "",
          handle: result.handle || "",
        }))
        .filter((product) => product.handle !== "" && product.id !== ""),
    [results]
  )

  // Filter products that we don't already have pricing data for
  const productsPricingToFetch = useMemo(() => {
    const cachedHandles = new Set(pricingCache.map((item) => item.handle))
    return formattedProductData.filter(
      (product) => !cachedHandles.has(product.handle)
    )
  }, [formattedProductData, pricingCache])

  // Only fetch prices for products not in cache
  const newPriceData = useShopifyPrice(
    productsPricingToFetch,
    currentCountryCurrency
  )

  // Update pricing cache when new price data arrives
  useEffect(() => {
    if (newPriceData.length > 0) {
      setPricingCache((prevCache) => {
        const existingMap = new Map(
          prevCache.map((item) => [item.handle, item])
        )

        newPriceData.forEach((item) => {
          existingMap.set(item.handle, item)
        })

        return Array.from(existingMap.values())
      })
    }
  }, [newPriceData])

  // Create new products array with updated price data or defaults back to results
  const productsWithUpdatedPrices = useMemo(() => {
    return mergeResultsWithPricing(results, pricingCache)
  }, [results, pricingCache])

  // Hide skeleton product cards when results are available
  useEffect(() => {
    const skeletonProductCardContainer = document.querySelector(
      ".main-collection-product-grid__skeleton"
    )

    if (
      (productsWithUpdatedPrices.length && skeletonProductCardContainer) ||
      (searchPage && productsWithUpdatedPrices.length === 0)
    ) {
      skeletonProductCardContainer?.classList.remove("is-visible-grid")
      skeletonProductCardContainer?.classList.add("is-hidden")
    }
  }, [productsWithUpdatedPrices])

  const handleClick = (event: MouseEvent) => {
    if (event?.target?.type === "checkbox") {
      setModelToggle(event?.target?.checked)
    }
  }

  useEffect(() => {
    const handleDelegatedClick = (event: MouseEvent) => {
      const toggle = event?.target?.closest(".collection-banner--model-toggle")
      if (toggle) {
        handleClick(event)
      }
    }

    document.addEventListener("click", handleDelegatedClick)

    return () => {
      document.removeEventListener("click", handleDelegatedClick)
    }
  }, [])

  useEffect(() => {
    const savedView = session.imageTypeView.value || true
    setModelToggle(savedView)
  }, [])

  useEffect(() => {
    session.imageTypeView.value = modelToggle
  }, [modelToggle])

  if (!productsWithUpdatedPrices.length) return null

  return (
    <div className="ss-product-grid">
      {productsWithUpdatedPrices.map(
        (result: string | SearchspringSearchResult) => {
          if (typeof result === "string")
            return (
              <div key={result} dangerouslySetInnerHTML={{ __html: result }} />
            )
          else
            return (
              <div key={result.handle}>
                <CardProduct
                  source="searchspring"
                  data={result}
                  modelToggle={modelToggle}
                  swatchColours={swatchColourData}
                  currentCountryCurrency={currentCountryCurrency}
                />
              </div>
            )
        }
      )}
    </div>
  )
}
